import { useState } from "react";
import type {
  CarrierDto,
  ShopDto,
} from "@babackapp/private-api-client/types";
import { fetchClient } from "@babackapp/private-api-client/generated/client";
import useTranslation from "@babackapp/translations/use-translation";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { Input } from "@babackapp/ui/components/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import {
  FEDEX_DOMESTIC_SERVICES,
  FEDEX_INTERNATIONAL_SERVICES,
  FEDEX_PICKUP_DROPOFF,
  FEDEX_PICKUP_SCHEDULED,
} from "../fedex-plans";
import { RatesTable, type RateDetail } from "../forms/fedex-rates-display";

type RateResponse = {
  transactionId?: string;
  output?: { rateReplyDetails?: RateDetail[] };
  errors?: { code: string; message: string }[];
};

type PickupResult = {
  pickupType: string;
  labelKey: "pickupScheduled" | "pickupDropoff";
  data?: RateResponse;
  error?: string;
};

const ALL_SERVICES = [
  ...FEDEX_DOMESTIC_SERVICES,
  ...FEDEX_INTERNATIONAL_SERVICES,
];

const PICKUPS = [
  { pickupType: FEDEX_PICKUP_SCHEDULED, labelKey: "pickupScheduled" as const },
  { pickupType: FEDEX_PICKUP_DROPOFF, labelKey: "pickupDropoff" as const },
];

function extractApiMessage(m: unknown): string | undefined {
  if (typeof m === "string") return m;
  if (Array.isArray(m)) {
    const parts = m
      .map((x) =>
        typeof x === "string"
          ? x
          : x && typeof x === "object" && "message" in x
            ? String((x as { message: unknown }).message)
            : "",
      )
      .filter(Boolean);
    return parts.length ? parts.join(", ") : undefined;
  }
  return undefined;
}

// Lowest quoted total for a pickup result, used to flag the cheaper option.
function minTotal(data?: RateResponse): number | undefined {
  const totals = (data?.output?.rateReplyDetails ?? [])
    .map((r) => r.ratedShipmentDetails?.[0]?.totalNetCharge)
    .filter((n): n is number => typeof n === "number");
  return totals.length ? Math.min(...totals) : undefined;
}

// Self-service FedEx return-rate viewer for shop admins/owners. Origin = the
// customer's country (where the return ships from); destination = the shop's
// return warehouse (fixed). Since this is a returns product there is no DDP/DAP
// choice and the service is auto-resolved (International Economy cross-border,
// else the carrier's domestic default). Fires one rate call per pickup type so
// the merchant can compare Scheduled pickup vs Drop-off.
export function FedexRatesButton({
  shopId,
  carrier,
  shop,
}: {
  shopId: string;
  carrier: CarrierDto;
  shop: ShopDto;
}) {
  const t = useTranslation();

  const defaultLocation =
    shop.locations.find((l) => l.isDefault) ?? shop.locations[0];
  const destinationCountry =
    defaultLocation?.address?.country_code ??
    carrier.destinationAccepted[0] ??
    "FR";
  const destinationPostal = defaultLocation?.address?.zip ?? "";

  const originOptions = carrier.originAccepted.length
    ? carrier.originAccepted
    : [destinationCountry];

  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<PickupResult[] | null>(null);
  const [originCountry, setOriginCountry] = useState(
    () => originOptions[0] ?? destinationCountry,
  );
  const [originPostal, setOriginPostal] = useState("");
  const [weightValue, setWeightValue] = useState("1");
  const [weightUnit, setWeightUnit] = useState<"KG" | "LB">("KG");

  const isInternational = originCountry !== destinationCountry;
  const serviceType = isInternational
    ? "INTERNATIONAL_ECONOMY"
    : carrier.preferences?.defaultServiceType || "FEDEX_2_DAY";
  const serviceLabel =
    ALL_SERVICES.find((s) => s.serviceType === serviceType)?.label ??
    serviceType;

  const fetchForPickup = async (
    pickup: (typeof PICKUPS)[number],
  ): Promise<PickupResult> => {
    try {
      const {
        data: res,
        error: err,
        response,
      } = await fetchClient.POST(
        `/shops/${shopId}/carriers/fedex/rates` as never,
        {
          body: {
            carrierId: carrier.id,
            origin: {
              postalCode: originPostal,
              countryCode: originCountry,
            },
            destination: {
              postalCode: destinationPostal,
              countryCode: destinationCountry,
            },
            weight: {
              value: Number(weightValue) || 1,
              units: weightUnit,
            },
            serviceType,
            pickupType: pickup.pickupType,
          },
        } as never,
      );
      if (err || !response.ok) {
        const e = err as
          | { message?: unknown; errors?: { message?: string }[] }
          | undefined;
        const msg = extractApiMessage(e?.message) ?? e?.errors?.[0]?.message;
        throw new Error(msg || `HTTP ${response.status}`);
      }
      return { ...pickup, data: res as RateResponse };
    } catch (e) {
      return { ...pickup, error: (e as Error).message };
    }
  };

  const handleGetRates = async () => {
    setOpen(true);
    setLoading(true);
    setResults(null);
    const next = await Promise.all(PICKUPS.map(fetchForPickup));
    setResults(next);
    setLoading(false);
  };

  const cheaperPickupType = (() => {
    if (!results) return undefined;
    const withTotals = results
      .map((r) => ({ pickupType: r.pickupType, total: minTotal(r.data) }))
      .filter(
        (r): r is { pickupType: string; total: number } =>
          typeof r.total === "number",
      );
    if (withTotals.length < 2) return undefined;
    return withTotals.reduce((a, b) => (b.total < a.total ? b : a)).pickupType;
  })();

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={loading}
        onClick={() => setOpen(true)}
      >
        {t("carrier.fedexCsp.getRates")}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <img
              src="/fedex.png"
              alt="FedEx"
              className="h-8 w-8 self-start object-contain mb-2 p-2 box-content"
            />
            <DialogTitle>{t("carrier.fedexCsp.ratesTitle")}</DialogTitle>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.origin")}
              </label>
              <Select value={originCountry} onValueChange={setOriginCountry}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {originOptions.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.postalCode")}
              </label>
              <Input
                value={originPostal}
                onChange={(e) => setOriginPostal(e.target.value)}
                placeholder="75001"
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.destination")}
              </label>
              <Input
                value={`${destinationCountry} ${destinationPostal}`.trim()}
                readOnly
                disabled
              />
            </div>
            <div className="space-y-1.5">
              <label className="block text-sm font-medium text-gray-700">
                {t("carrier.fedexCsp.weight")}
              </label>
              <div className="flex gap-2">
                <Input
                  type="number"
                  min="0"
                  step="0.1"
                  value={weightValue}
                  onChange={(e) => setWeightValue(e.target.value)}
                />
                <Select
                  value={weightUnit}
                  onValueChange={(v) => setWeightUnit(v as "KG" | "LB")}
                >
                  <SelectTrigger className="w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="KG">KG</SelectItem>
                    <SelectItem value="LB">LB</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <p className="text-xs text-gray-500">
            {t("carrier.fedexCsp.service")}: {serviceLabel}
          </p>

          <Button size="sm" disabled={loading} onClick={handleGetRates}>
            {t("carrier.fedexCsp.getRates")}
          </Button>

          {loading && (
            <p className="text-sm text-gray-500">
              {t("carrier.fedexCsp.ratesLoading")}
            </p>
          )}

          {results && !loading && (
            <div className="space-y-4">
              {results.map((r) => {
                const apiError = r.data?.errors?.[0];
                return (
                  <div key={r.pickupType} className="space-y-2">
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-semibold text-gray-800">
                        {t(`carrier.fedexCsp.${r.labelKey}`)}
                      </h4>
                      {cheaperPickupType === r.pickupType && (
                        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">
                          {t("carrier.fedexCsp.cheaper")}
                        </span>
                      )}
                    </div>
                    {r.error && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                        {r.error}
                      </div>
                    )}
                    {apiError && (
                      <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
                        {apiError.code}: {apiError.message}
                      </div>
                    )}
                    {r.data && !apiError && (
                      <RatesTable
                        rates={r.data.output?.rateReplyDetails ?? []}
                      />
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
